import { useState, useCallback, useEffect } from 'react';

// const API_URL = 'http://localhost:3001/api';
const API_URL = 'https://med-bot.dev-alb.ru/api';

export const useCart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  const loadCart = useCallback(async (uid) => {
    if (!uid) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/${uid}`);
      if (response.ok) {
        const data = await response.json();
        const items = data.map(item => ({
          id: item.id,
          service_id: item.service_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          description: item.description
        }));
        setCartItems(items);
      }
    } catch (error) {
      console.error('Ошибка загрузки корзины:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const setUser = useCallback((uid) => {
    setUserId(uid);
    if (uid) {
      loadCart(uid);
    } else {
      setCartItems([]);
    }
  }, [loadCart]);

  const addToCart = useCallback(async (item) => {
    if (!userId) {
      console.warn('Пользователь не авторизован');
      return false;
    }

    const tempId = Date.now() + Math.random();
    const newItem = {
      id: tempId,
      service_id: item.id || item.service_id,
      name: item.name,
      price: item.price,
      quantity: item.quantity || 1
    };

    setCartItems(prev => {
      const existingIndex = prev.findIndex(i => 
        i.service_id === newItem.service_id
      );
      
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + (item.quantity || 1)
        };
        return updated;
      }
      return [...prev, newItem];
    });

    try {
      const response = await fetch(`${API_URL}/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          service_id: item.id || item.service_id,
          quantity: item.quantity || 1
        })
      });

      if (!response.ok) {
        throw new Error('Ошибка добавления в корзину');
      }

      await loadCart(userId);
      return true;
    } catch (error) {
      console.error('Ошибка добавления в корзину:', error);
      await loadCart(userId);
      return false;
    }
  }, [userId, loadCart]);

  const addMultipleToCart = useCallback(async (items) => {
    if (!userId || !items.length) return false;

    setCartItems(prev => {
      const newItems = [...prev];
      items.forEach(item => {
        const existingIndex = newItems.findIndex(i => 
          i.service_id === (item.id || item.service_id)
        );
        if (existingIndex !== -1) {
          newItems[existingIndex] = {
            ...newItems[existingIndex],
            quantity: newItems[existingIndex].quantity + (item.quantity || 1)
          };
        } else {
          newItems.push({
            id: Date.now() + Math.random(),
            service_id: item.id || item.service_id,
            name: item.name,
            price: item.price,
            quantity: item.quantity || 1
          });
        }
      });
      return newItems;
    });

    try {
      await Promise.all(items.map(item => 
        fetch(`${API_URL}/cart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            service_id: item.id || item.service_id,
            quantity: item.quantity || 1
          })
        })
      ));
      
      await loadCart(userId);
      return true;
    } catch (error) {
      console.error('Ошибка добавления товаров:', error);
      await loadCart(userId);
      return false;
    }
  }, [userId, loadCart]);

  const removeFromCart = useCallback(async (itemId) => {
    if (!userId) return;

    const removedItem = cartItems.find(i => i.id === itemId);
    setCartItems(prev => prev.filter(item => item.id !== itemId));

    try {
      const response = await fetch(`${API_URL}/cart/${itemId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Ошибка удаления');
      }
    } catch (error) {
      console.error('Ошибка удаления:', error);
      if (removedItem) {
        setCartItems(prev => [...prev, removedItem]);
      }
    }
  }, [userId, cartItems]);

  const updateQuantity = useCallback(async (itemId, newQuantity) => {
    if (!userId) return;
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    const oldItem = cartItems.find(i => i.id === itemId);
    setCartItems(prev =>
      prev.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );

    try {
      const response = await fetch(`${API_URL}/cart/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQuantity })
      });

      if (!response.ok) {
        throw new Error('Ошибка обновления количества');
      }
    } catch (error) {
      console.error('Ошибка обновления:', error);
      if (oldItem) {
        setCartItems(prev =>
          prev.map(item =>
            item.id === itemId ? { ...item, quantity: oldItem.quantity } : item
          )
        );
      }
    }
  }, [userId, cartItems, removeFromCart]);

  const clearCart = useCallback(async () => {
    if (!userId) return;

    setCartItems([]);

    try {
      const response = await fetch(`${API_URL}/cart/clear/${userId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Ошибка очистки корзины');
      }
    } catch (error) {
      console.error('Ошибка очистки:', error);
      await loadCart(userId);
    }
  }, [userId, loadCart]);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      const price = typeof item.price === 'string' 
        ? parseFloat(item.price.replace(/[^\d.]/g, '')) || 0
        : item.price || 0;
      return total + (price * (item.quantity || 1));
    }, 0);
  }, [cartItems]);

  const getItemsCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + (item.quantity || 1), 0);
  }, [cartItems]);

  return {
    cartItems,
    loading,
    setUser,
    addToCart,
    addMultipleToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getItemsCount
  };
};